import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, RefreshCwIcon, FileEditIcon, FilterIcon, XIcon, ChevronDownIcon, LockIcon, UserIcon, SettingsIcon, CheckIcon, GripVerticalIcon } from 'lucide-react';
import { Invoice } from '../types/invoice';
import { Badge } from './ui/Badge';
interface DocumentSidebarProps {
  documents: Invoice[];
  selectedDocumentId: string;
  onSelectDocument: (id: string) => void;
  currentDocument?: any; // The actual document data with draft changes
}
// Helper to check if document has draft
const hasDraft = (documentId: string): boolean => {
  return localStorage.getItem(`invoice_draft_${documentId}`) !== null;
};
type FilterCategory = 'type' | 'status' | 'team';
interface ActiveFilters {
  type: string[];
  status: string[];
  team: string[];
}
const FILTERS_STORAGE_KEY = 'document_filters';
// Tile field configuration types
type DocumentType = 'PO Invoice' | 'Non-PO Invoice' | 'Credit Note';
interface TileField {
  id: string;
  label: string;
  enabled: boolean;
  order: number;
}
interface TileConfiguration {
  [key: string]: TileField[]; // key is document type
}
const TILE_CONFIG_STORAGE_KEY = 'document_tile_config';
// Default field configurations for each document type
const DEFAULT_TILE_CONFIG: TileConfiguration = {
  'PO Invoice': [{
    id: 'vendor',
    label: 'Vendor',
    enabled: true,
    order: 0
  }, {
    id: 'documentNo',
    label: 'Document Number',
    enabled: true,
    order: 1
  }, {
    id: 'receivedDate',
    label: 'Received Date',
    enabled: true,
    order: 2
  }, {
    id: 'documentDate',
    label: 'Document Date',
    enabled: true,
    order: 3
  }, {
    id: 'dueDate',
    label: 'Due Date',
    enabled: true,
    order: 4
  }, {
    id: 'poNo',
    label: 'PO Number',
    enabled: true,
    order: 5
  }, {
    id: 'team',
    label: 'Assigned To',
    enabled: true,
    order: 6
  }, {
    id: 'amount',
    label: 'Amount',
    enabled: true,
    order: 7
  }, {
    id: 'tag',
    label: 'Tag(s)',
    enabled: false,
    order: 8
  }],
  'Non-PO Invoice': [{
    id: 'vendor',
    label: 'Vendor',
    enabled: true,
    order: 0
  }, {
    id: 'documentNo',
    label: 'Document Number',
    enabled: true,
    order: 1
  }, {
    id: 'receivedDate',
    label: 'Received Date',
    enabled: true,
    order: 2
  }, {
    id: 'documentDate',
    label: 'Document Date',
    enabled: true,
    order: 3
  }, {
    id: 'dueDate',
    label: 'Due Date',
    enabled: true,
    order: 4
  }, {
    id: 'team',
    label: 'Assigned To',
    enabled: true,
    order: 5
  }, {
    id: 'amount',
    label: 'Amount',
    enabled: true,
    order: 6
  }, {
    id: 'tag',
    label: 'Tag(s)',
    enabled: false,
    order: 7
  }, {
    id: 'poNo',
    label: 'PO Number',
    enabled: false,
    order: 8
  }],
  'Credit Note': [{
    id: 'vendor',
    label: 'Vendor',
    enabled: true,
    order: 0
  }, {
    id: 'documentNo',
    label: 'Document Number',
    enabled: true,
    order: 1
  }, {
    id: 'receivedDate',
    label: 'Received Date',
    enabled: false,
    order: 2
  }, {
    id: 'documentDate',
    label: 'Document Date',
    enabled: true,
    order: 3
  }, {
    id: 'dueDate',
    label: 'Due Date',
    enabled: false,
    order: 4
  }, {
    id: 'poNo',
    label: 'PO Number',
    enabled: false,
    order: 5
  }, {
    id: 'team',
    label: 'Assigned To',
    enabled: true,
    order: 6
  }, {
    id: 'amount',
    label: 'Credit Amount',
    enabled: true,
    order: 7
  }, {
    id: 'tag',
    label: 'Tag(s)',
    enabled: false,
    order: 8
  }]
};
// Load tile configuration from localStorage
const loadTileConfig = (): TileConfiguration => {
  try {
    const saved = localStorage.getItem(TILE_CONFIG_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Failed to load tile config:', error);
  }
  return DEFAULT_TILE_CONFIG;
};
// Save tile configuration to localStorage
const saveTileConfig = (config: TileConfiguration) => {
  try {
    localStorage.setItem(TILE_CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('Failed to save tile config:', error);
  }
};
// Helper to load filters from localStorage
const loadSavedFilters = (): ActiveFilters => {
  try {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Failed to load saved filters:', error);
  }
  return {
    type: [],
    status: [],
    team: []
  };
};
// Helper to save filters to localStorage
const saveFilters = (filters: ActiveFilters) => {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.error('Failed to save filters:', error);
  }
};
export function DocumentSidebar({
  documents,
  selectedDocumentId,
  onSelectDocument,
  currentDocument
}: DocumentSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(loadSavedFilters());
  // Tile configuration state
  const [tileConfig, setTileConfig] = useState<TileConfiguration>(loadTileConfig());
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configDocumentType, setConfigDocumentType] = useState<DocumentType>('PO Invoice');
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    documentType: DocumentType;
  } | null>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  // Save filters to localStorage whenever they change
  useEffect(() => {
    saveFilters(activeFilters);
  }, [activeFilters]);
  // Save tile config whenever it changes
  useEffect(() => {
    saveTileConfig(tileConfig);
  }, [tileConfig]);
  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        // Check if click is on the filter button itself
        const filterButton = document.querySelector('[data-filter-button]');
        if (filterButton && filterButton.contains(event.target as Node)) {
          return; // Don't close if clicking the button - let the button's onClick handle it
        }
        setShowFilterMenu(false);
      }
    };
    if (showFilterMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterMenu]);
  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(null);
      }
    };
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu]);
  // Handle right-click on document tile
  const handleContextMenu = (e: React.MouseEvent, documentType: DocumentType) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      documentType
    });
  };
  // Open configuration modal
  const openConfigModal = (documentType: DocumentType) => {
    setConfigDocumentType(documentType);
    setShowConfigModal(true);
    setContextMenu(null);
  };
  // Toggle field enabled/disabled
  const toggleField = (documentType: DocumentType, fieldId: string) => {
    setTileConfig(prev => ({
      ...prev,
      [documentType]: prev[documentType].map(field => field.id === fieldId ? {
        ...field,
        enabled: !field.enabled
      } : field)
    }));
  };
  // Reset to default configuration
  const resetToDefault = (documentType: DocumentType) => {
    setTileConfig(prev => ({
      ...prev,
      [documentType]: DEFAULT_TILE_CONFIG[documentType]
    }));
  };
  // Get field value from document
  const getFieldValue = (document: Invoice, fieldId: string): string | null => {
    switch (fieldId) {
      case 'vendor':
        return document.vendor;
      case 'documentNo':
        return document.documentNo || null;
      case 'receivedDate':
        return document.receivedDate;
      case 'documentDate':
        return document.documentDate;
      case 'dueDate':
        return document.dueDate || null;
      case 'poNo':
        return document.poNo || null;
      case 'team':
        return document.team;
      case 'amount':
        return document.amount;
      case 'tag':
        return document.tag || null;
      default:
        return null;
    }
  };
  // Get unique teams from documents
  const uniqueTeams = Array.from(new Set(documents.map(i => i.team)));
  // Get unique statuses
  const uniqueStatuses = Array.from(new Set(documents.map(i => i.status)));
  // Filter documents based on active filters and search
  const filteredDocuments = documents.filter(document => {
    // Apply type filters
    if (activeFilters.type.length > 0) {
      const matchesType = activeFilters.type.some(type => {
        if (type === 'po') return document.documentType === 'PO Invoice';
        if (type === 'non-po') return document.documentType === 'Non-PO Invoice';
        if (type === 'credit-note') return document.documentType === 'Credit Note';
        if (type === 'draft') return hasDraft(document.id) || document.status.toLowerCase().includes('draft');
        return false;
      });
      if (!matchesType) return false;
    }
    // Apply status filters
    if (activeFilters.status.length > 0) {
      const matchesStatus = activeFilters.status.includes(document.status);
      if (!matchesStatus) return false;
    }
    // Apply team filters
    if (activeFilters.team.length > 0) {
      const matchesTeam = activeFilters.team.includes(document.team);
      if (!matchesTeam) return false;
    }
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = document.vendor.toLowerCase().includes(query) || document.documentNo?.toLowerCase().includes(query) || document.poNo?.toLowerCase().includes(query) || document.amount.toLowerCase().includes(query);
      return matchesSearch;
    }
    return true;
  });
  const toggleFilter = (category: FilterCategory, value: string) => {
    setActiveFilters(prev => {
      const current = prev[category];
      const newFilters = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return {
        ...prev,
        [category]: newFilters
      };
    });
  };
  const clearAllFilters = () => {
    const emptyFilters = {
      type: [],
      status: [],
      team: []
    };
    setActiveFilters(emptyFilters);
    saveFilters(emptyFilters);
  };
  const hasActiveFilters = activeFilters.type.length > 0 || activeFilters.status.length > 0 || activeFilters.team.length > 0;
  const activeFilterCount = activeFilters.type.length + activeFilters.status.length + activeFilters.team.length;
  return <div className="w-72 bg-white border-r border-gray-200 flex flex-col h-screen" data-id="element-734">
      {/* Header */}
      <div className="p-4 border-b border-gray-200" data-id="element-735">
        <div className="flex items-center justify-between mb-3" data-id="element-736">
          <span className="text-sm text-gray-600" data-id="element-737">
            {filteredDocuments.length} of {documents.length}
          </span>
          <button className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-738">
            <RefreshCwIcon className="w-4 h-4 text-gray-600" data-id="element-739" />
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-3" data-id="element-740">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-741" />
          <input type="text" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#7CB342] focus:border-transparent" data-id="element-742" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded transition-colors" data-id="element-743">
              <XIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-744" />
            </button>}
        </div>

        {/* Filter Button */}
        <div className="relative" ref={filterMenuRef} data-id="element-745">
          <button data-filter-button onClick={() => setShowFilterMenu(!showFilterMenu)} className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm font-medium transition-colors ${hasActiveFilters ? 'bg-[#7CB342] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-746">
            <div className="flex items-center gap-2" data-id="element-747">
              <FilterIcon className="w-4 h-4" data-id="element-748" />
              <span data-id="element-749">Filters</span>
              {activeFilterCount > 0 && <span className="px-1.5 py-0.5 bg-white/20 rounded text-xs" data-id="element-750">
                  {activeFilterCount}
                </span>}
            </div>
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${showFilterMenu ? 'rotate-180' : ''}`} data-id="element-751" />
          </button>

          {/* Filter Dropdown Menu */}
          {showFilterMenu && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto" ref={filterMenuRef} data-id="element-752">
              {/* Document Type Section */}
              <div className="p-3 border-b border-gray-100" data-id="element-753">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2" data-id="element-754">
                  Document Type
                </div>
                <div className="space-y-1.5" data-id="element-755">
                  {[{
                id: 'po',
                label: 'PO Invoice',
                count: documents.filter(i => i.documentType === 'PO Invoice').length
              }, {
                id: 'non-po',
                label: 'Non-PO Invoice',
                count: documents.filter(i => i.documentType === 'Non-PO Invoice').length
              }, {
                id: 'credit-note',
                label: 'Credit Note',
                count: documents.filter(i => i.documentType === 'Credit Note').length
              }, {
                id: 'draft',
                label: 'Has Draft',
                count: documents.filter(i => hasDraft(i.id) || i.status.toLowerCase().includes('draft')).length
              }].map(option => <label key={option.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer" data-id="element-756">
                      <input type="checkbox" checked={activeFilters.type.includes(option.id)} onChange={() => toggleFilter('type', option.id)} className="w-4 h-4 text-[#7CB342] border-gray-300 rounded focus:ring-[#7CB342]" data-id="element-757" />
                      <span className="text-sm text-gray-700 flex-1" data-id="element-758">
                        {option.label}
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-759">
                        ({option.count})
                      </span>
                    </label>)}
                </div>
              </div>

              {/* Status Section */}
              <div className="p-3 border-b border-gray-100" data-id="element-760">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2" data-id="element-761">
                  Status
                </div>
                <div className="space-y-1.5" data-id="element-762">
                  {uniqueStatuses.map(status => {
                const count = documents.filter(i => i.status === status).length;
                return <label key={status} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer" data-id="element-763">
                        <input type="checkbox" checked={activeFilters.status.includes(status)} onChange={() => toggleFilter('status', status)} className="w-4 h-4 text-[#7CB342] border-gray-300 rounded focus:ring-[#7CB342]" data-id="element-764" />
                        <span className="text-sm text-gray-700 flex-1" data-id="element-765">
                          {status}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-766">({count})</span>
                      </label>;
              })}
                </div>
              </div>

              {/* Team/Assigned Section */}
              <div className="p-3" data-id="element-767">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2" data-id="element-768">
                  Assigned To
                </div>
                <div className="space-y-1.5" data-id="element-769">
                  {uniqueTeams.map(team => {
                const count = documents.filter(i => i.team === team).length;
                return <label key={team} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer" data-id="element-770">
                        <input type="checkbox" checked={activeFilters.team.includes(team)} onChange={() => toggleFilter('team', team)} className="w-4 h-4 text-[#7CB342] border-gray-300 rounded focus:ring-[#7CB342]" data-id="element-771" />
                        <span className="text-sm text-gray-700 flex-1" data-id="element-772">
                          {team}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-773">({count})</span>
                      </label>;
              })}
                </div>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && <div className="p-3 border-t border-gray-100" data-id="element-774">
                  <button onClick={clearAllFilters} className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" data-id="element-775">
                    Clear All Filters
                  </button>
                </div>}
            </div>}
        </div>

        {/* Active Filter Tags */}
        {hasActiveFilters && <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-776">
            {activeFilters.type.map(type => <button key={type} onClick={() => toggleFilter('type', type)} className="inline-flex items-center gap-1 px-2 py-1 bg-[#7CB342] text-white text-xs rounded" data-id="element-777">
                <span data-id="element-778">
                  {type === 'po' && 'PO Invoice'}
                  {type === 'non-po' && 'Non-PO'}
                  {type === 'credit-note' && 'Credit'}
                  {type === 'draft' && 'Draft'}
                </span>
                <XIcon className="w-3 h-3" data-id="element-779" />
              </button>)}
            {activeFilters.status.map(status => <button key={status} onClick={() => toggleFilter('status', status)} className="inline-flex items-center gap-1 px-2 py-1 bg-[#7CB342] text-white text-xs rounded" data-id="element-780">
                <span data-id="element-781">{status}</span>
                <XIcon className="w-3 h-3" data-id="element-782" />
              </button>)}
            {activeFilters.team.map(team => <button key={team} onClick={() => toggleFilter('team', team)} className="inline-flex items-center gap-1 px-2 py-1 bg-[#7CB342] text-white text-xs rounded" data-id="element-783">
                <span data-id="element-784">{team}</span>
                <XIcon className="w-3 h-3" data-id="element-785" />
              </button>)}
          </div>}
      </div>

      {/* Document List */}
      <div className="flex-1 overflow-y-auto" data-id="element-786">
        {filteredDocuments.length === 0 ? <div className="flex flex-col items-center justify-center py-12 px-4 text-center" data-id="element-787">
            <FilterIcon className="w-8 h-8 text-gray-300 mb-2" data-id="element-788" />
            <p className="text-sm text-gray-500" data-id="element-789">No documents found</p>
            <p className="text-xs text-gray-400 mt-1" data-id="element-790">
              Try adjusting your filters or search
            </p>
          </div> : filteredDocuments.map(document => {
        const isDraft = hasDraft(document.id);
        const isLocked = !!document.lockedBy;
        const isLockedByCurrentUser = document.lockedBy === 'Current User';
        const documentType = document.documentType as DocumentType;
        const config = tileConfig[documentType] || [];
        const enabledFields = config.filter(field => field.enabled).sort((a, b) => a.order - b.order);
        return <div key={document.id} onClick={() => onSelectDocument(document.id)} onContextMenu={e => handleContextMenu(e, documentType)} className={`p-4 border-b border-gray-200 cursor-pointer transition-colors ${selectedDocumentId === document.id ? 'bg-[#F1F8E9] border-l-4 border-l-[#7CB342]' : 'hover:bg-gray-50'} ${isLocked && !isLockedByCurrentUser ? 'opacity-60' : ''}`} data-id="element-791">
                {/* Locked Banner */}
                {isLocked && <div className={`flex items-center gap-2 mb-2 px-2 py-1.5 rounded text-xs font-medium ${isLockedByCurrentUser ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-792">
                    <LockIcon className="w-3.5 h-3.5" data-id="element-793" />
                    <UserIcon className="w-3.5 h-3.5" data-id="element-794" />
                    <span data-id="element-795">
                      {isLockedByCurrentUser ? 'You are editing this' : `Being edited by ${document.lockedBy}`}
                    </span>
                  </div>}

                {/* First Line: Document Type (left) and Status Badge (right) */}
                <div className="flex items-center justify-between mb-2" data-id="element-796">
                  <div className="text-sm font-medium text-gray-900" data-id="element-797">
                    {/* Show updated document type if this is the selected document with draft changes */}
                    {selectedDocumentId === document.id && currentDocument?.matchingType ? currentDocument.matchingType === '2-way' || currentDocument.matchingType === '3-way' ? 'PO Invoice' : 'Non-PO Invoice' : document.documentType}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-798">
                    <Badge status={document.status} data-id="element-799">{document.status}</Badge>
                    {isDraft && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 rounded" data-id="element-800">
                        <FileEditIcon size={10} className="text-amber-700" data-id="element-801" />
                        <span className="text-xs font-medium text-amber-700" data-id="element-802">
                          Draft
                        </span>
                      </div>}
                  </div>
                </div>

                {/* Configurable Fields */}
                <div className="space-y-1 text-xs text-gray-600" data-id="element-803">
                  {enabledFields.map(field => {
              const value = getFieldValue(document, field.id);
              if (!value) return null;
              const isAmount = field.id === 'amount';
              const isVendor = field.id === 'vendor';
              const isTeam = field.id === 'team';
              return <div key={field.id} className="flex justify-between" data-id="element-804">
                        <span data-id="element-805">{field.label}:</span>
                        <span className={`${isAmount || isVendor || isTeam ? 'font-medium text-gray-900' : ''}`} data-id="element-806">
                          {value}
                        </span>
                      </div>;
            })}
                </div>

                <div className="mt-2 text-xs text-gray-500" data-id="element-807">
                  {document.timestamp}
                </div>
              </div>;
      })}
      </div>

      {/* Context Menu */}
      {contextMenu && <div ref={contextMenuRef} className="fixed bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-50 min-w-[180px]" style={{
      left: `${contextMenu.x}px`,
      top: `${contextMenu.y}px`
    }} data-id="element-808">
          <button onClick={() => openConfigModal(contextMenu.documentType)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors" data-id="element-809">
            <SettingsIcon className="w-4 h-4" data-id="element-810" />
            <span data-id="element-811">Configure {contextMenu.documentType} Tiles</span>
          </button>
        </div>}

      {/* Configuration Modal */}
      {showConfigModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-812">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col" data-id="element-813">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-814">
              <div data-id="element-815">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-816">
                  Configure Tile Fields
                </h2>
                <p className="text-sm text-gray-600 mt-1" data-id="element-817">
                  {configDocumentType}
                </p>
              </div>
              <button onClick={() => setShowConfigModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-818">
                <XIcon className="w-5 h-5 text-gray-600" data-id="element-819" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6" data-id="element-820">
              <p className="text-sm text-gray-600 mb-4" data-id="element-821">
                Select which fields to display on document tiles. Enabled fields
                will appear in the order shown below.
              </p>

              <div className="space-y-2" data-id="element-822">
                {tileConfig[configDocumentType]?.sort((a, b) => a.order - b.order).map(field => <label key={field.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-823">
                      <input type="checkbox" checked={field.enabled} onChange={() => toggleField(configDocumentType, field.id)} className="w-4 h-4 text-[#7CB342] border-gray-300 rounded focus:ring-[#7CB342]" data-id="element-824" />
                      <GripVerticalIcon className="w-4 h-4 text-gray-400" data-id="element-825" />
                      <span className="flex-1 text-sm text-gray-900" data-id="element-826">
                        {field.label}
                      </span>
                      {field.enabled && <CheckIcon className="w-4 h-4 text-[#7CB342]" data-id="element-827" />}
                    </label>)}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-828">
              <button onClick={() => resetToDefault(configDocumentType)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded transition-colors" data-id="element-829">
                Reset to Default
              </button>
              <button onClick={() => setShowConfigModal(false)} className="px-4 py-2 text-sm font-medium text-white bg-[#7CB342] hover:bg-[#689F38] rounded transition-colors" data-id="element-830">
                Done
              </button>
            </div>
          </div>
        </div>}
    </div>;
}